import { useMemo, useState } from "react";
import {
  Plus, Trash2, MessageCircle, Printer, Tag, Eye, ArrowLeft, TruckIcon, CheckCircle2, Clock,
  Search, ArrowUp, ArrowDown, ArrowUpDown,
} from "lucide-react";
import { STATUS_CONFIG, PAY_LABELS, PAY_COLORS, fmtBRL, monthLabel, waLink, HEADER_BG } from "../lib/constants";
import { printOrderOS, printOrderLabel } from "./printOS";
import ConfirmDialog from "../components/ConfirmDialog";

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status];
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${cfg.bg} ${cfg.text} ${cfg.border}`}
    >
      {status === "received" && <Clock className="w-3 h-3" />}
      {status === "ready" && <CheckCircle2 className="w-3 h-3" />}
      {status === "delivered" && <TruckIcon className="w-3 h-3" />}
      {cfg.label}
    </span>
  );
}

function PayBadge({ payMethod, cartaoDesc }) {
  if (!payMethod) return <span className="text-[10px] italic text-slate-300">—</span>;
  const pc = PAY_COLORS[payMethod];
  return (
    <span
      className="text-[10px] font-bold px-2 py-0.5 rounded-full border"
      style={{ background: pc.bg, color: pc.color, borderColor: pc.border }}
    >
      {PAY_LABELS[payMethod]}
      {payMethod === "cartao" && cartaoDesc ? ` · ${cartaoDesc}` : ""}
    </span>
  );
}

function PayEditor({ order, onSave, onCancel }) {
  const [pay, setPay] = useState(order.payMethod || "");
  const [desc, setDesc] = useState(order.cartaoDesc || "");  return (
    <div className="flex items-center gap-1">
      <select
        autoFocus
        value={pay}
        onChange={(e) => {
          const v = e.target.value;
          setPay(v);
          if (v !== "cartao") onSave(v || null, "");
        }}
        className="text-[10px] rounded border border-slate-300 px-1 py-0.5 focus:outline-none focus:border-blue-500 bg-white"
      >
        <option value="">Selecione…</option>
        {Object.entries(PAY_LABELS).map(([k, v]) => (
          <option key={k} value={k}>
            {v}
          </option>
        ))}
      </select>
      {pay === "cartao" && (
        <input
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
          onBlur={() => onSave("cartao", desc)}
          onKeyDown={(e) => e.key === "Enter" && onSave("cartao", desc)}
          placeholder="Desc."
          className="text-[10px] w-16 rounded border border-indigo-300 bg-indigo-50 text-indigo-800 px-1 py-0.5 focus:outline-none focus:border-indigo-500"
          autoFocus
        />
      )}
      <button className="text-[10px] text-slate-400" onClick={onCancel}>
        ×
      </button>
    </div>
  );
}

const HEADERS_FULL = [
  "DATA",
  "HORA",
  "CLIENTE",
  "APARELHO",
  "SERVIÇO",
  "VALOR",
  "CUSTO",
  "PAGAMENTO",
  "STATUS",
  "SAÍDA",
  "OBS.",
  "AÇÕES",
];

const SortIcon = ({ active, dir }) => {
  if (!active) return <ArrowUpDown className="w-3 h-3 opacity-40 inline ml-1" />;
  return dir === "desc" ? (
    <ArrowDown className="w-3 h-3 inline ml-1" />
  ) : (
    <ArrowUp className="w-3 h-3 inline ml-1" />
  );
};

// helper para converter data brasileira em número (para sort)
const dateToNum = (br) => {
  if (!br) return 0;
  const [d, m, y] = br.split("/");
  return (parseInt(y) || 0) * 10000 + (parseInt(m) || 0) * 100 + (parseInt(d) || 0);
};
const timeToNum = (t) => {
  if (!t) return 0;
  const [h, m] = t.split(":");
  return (parseInt(h) || 0) * 60 + (parseInt(m) || 0);
};

export default function OrdersView({
  orders,
  searchQuery,
  currentMonth,
  onNewOrder,
  onExit,
  onOpenDetail,
  onUpdateOrder,
  onRevertStatus,
  onDeleteOrder,
}) {
  // CUSTO is visible to all roles on Entrada (per user request) — kept editable inline
  const HEADERS = HEADERS_FULL;
  const [statusFilter, setStatusFilter] = useState("all");
  const [monthFilter, setMonthFilter] = useState("all");
  const [editingPayId, setEditingPayId] = useState(null);
  const [editingObsId, setEditingObsId] = useState(null);
  const [editingValueId, setEditingValueId] = useState(null);
  const [editingCostId, setEditingCostId] = useState(null);
  const [editingEntryDateId, setEditingEntryDateId] = useState(null);
  const [editingEntryTimeId, setEditingEntryTimeId] = useState(null);
  const [editingClientId, setEditingClientId] = useState(null);
  const [editingDeviceId, setEditingDeviceId] = useState(null);
  const [editingServicesId, setEditingServicesId] = useState(null);
  const [clientSearch, setClientSearch] = useState("");
  const [sort, setSort] = useState({ field: null, dir: "desc" });
  const [confirmDelete, setConfirmDelete] = useState(null);

  const toggleSort = (field) => {
    setSort((cur) => {
      if (cur.field !== field) return { field, dir: "desc" };
      if (cur.dir === "desc") return { field, dir: "asc" };
      return { field: null, dir: "desc" };
    });
  };

  const availableMonths = useMemo(() => {
    const seen = new Set();
    orders.forEach((o) => o.entryDate && seen.add(o.entryDate.slice(3, 10)));
    return Array.from(seen).sort();
  }, [orders]);

  const filtered = useMemo(() => {
    const q = (searchQuery || "").toLowerCase();
    const cq = clientSearch.trim().toLowerCase();
    let arr = orders.filter((o) => {
      if (statusFilter !== "all" && o.status !== statusFilter) return false;
      if (monthFilter !== "all" && o.entryDate?.slice(3, 10) !== monthFilter) return false;
      if (cq) {
        const match =
          o.clientName?.toLowerCase().includes(cq) ||
          o.clientPhone?.includes(clientSearch.trim());
        if (!match) return false;
      }
      if (!q) return true;
      return (
        o.clientName?.toLowerCase().includes(q) ||
        o.model?.toLowerCase().includes(q) ||
        o.imei?.toLowerCase().includes(q) ||
        o.clientPhone?.includes(q) ||
        o.orderNumber?.toLowerCase().includes(q)
      );
    });

    if (sort.field) {
      const dir = sort.dir === "asc" ? 1 : -1;
      arr = [...arr].sort((a, b) => {
        let av = 0;
        let bv = 0;
        if (sort.field === "entryDate") {
          av = dateToNum(a.entryDate) * 10000 + timeToNum(a.entryTime);
          bv = dateToNum(b.entryDate) * 10000 + timeToNum(b.entryTime);
        } else if (sort.field === "entryTime") {
          av = timeToNum(a.entryTime);
          bv = timeToNum(b.entryTime);
        } else if (sort.field === "exitDate") {
          av = dateToNum(a.exitDate) * 10000 + timeToNum(a.exitTime);
          bv = dateToNum(b.exitDate) * 10000 + timeToNum(b.exitTime);
        }
        return (av - bv) * dir;
      });
    }
    return arr;
  }, [orders, statusFilter, monthFilter, searchQuery, clientSearch, sort]);

  const counts = {
    all: orders.length,
    received: orders.filter((o) => o.status === "received").length,
    ready: orders.filter((o) => o.status === "ready").length,
    delivered: orders.filter((o) => o.status === "delivered").length,
  };

  const advance = (o) => {
    if (o.status === "received") onUpdateOrder(o.id, { status: "ready" });
    else if (o.status === "ready") onExit(o);
  };

  return (
    <div className="p-6 space-y-4" data-testid="orders-view">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold text-slate-900">Entrada (OS)</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            {filtered.length} {filtered.length === 1 ? "ordem" : "ordens"} ·{" "}
            {monthFilter === "all" ? "Todos os meses" : monthLabel(monthFilter)}
          </p>
        </div>
        <button
          onClick={onNewOrder}
          data-testid="new-os-button"
          className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-all hover:-translate-y-[1px] hover:shadow-lg"
        >
          <Plus className="w-4 h-4" /> Nova OS
        </button>
      </div>

      {/* Client search + Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none" />
          <input
            value={clientSearch}
            onChange={(e) => setClientSearch(e.target.value)}
            placeholder="Buscar cliente..."
            data-testid="orders-client-search"
            className="h-9 rounded-lg border border-slate-300 pl-8 pr-3 text-sm w-56 focus:border-blue-500 focus:outline-none bg-white"
          />
        </div>
        <div className="w-px h-6 bg-slate-200 mx-1" />
        <button
          onClick={() => setMonthFilter("all")}
          className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
            monthFilter === "all"
              ? "bg-slate-900 text-white"
              : "bg-white text-slate-600 border border-slate-200 hover:border-slate-300"
          }`}
        >
          Todos meses
        </button>
        {availableMonths.map((m) => (
          <button
            key={m}
            onClick={() => setMonthFilter(m)}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
              monthFilter === m
                ? "bg-slate-900 text-white"
                : "bg-white text-slate-600 border border-slate-200 hover:border-slate-300"
            }`}
          >
            {monthLabel(m)}
          </button>
        ))}
        <div className="w-px bg-slate-200 mx-1" />
        {[
          ["all", "Todos"],
          ["received", "Recebidos"],
          ["ready", "Prontos"],
          ["delivered", "Entregues"],
        ].map(([val, label]) => (
          <button
            key={val}
            onClick={() => setStatusFilter(val)}
            data-testid={`filter-${val}`}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all inline-flex items-center gap-1.5 ${
              statusFilter === val
                ? "bg-blue-600 text-white shadow-sm"
                : "bg-white text-slate-600 border border-slate-200 hover:border-blue-300 hover:text-blue-700"
            }`}
          >
            {label}
            <span
              className={`text-[10px] px-1.5 py-0.5 rounded ${
                statusFilter === val ? "bg-white/20" : "bg-slate-100 text-slate-500"
              }`}
            >
              {counts[val]}
            </span>
          </button>
        ))}
      </div>

      {/* Spreadsheet */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: HEADER_BG }}>
                {HEADERS.map((h) => {
                  const sortable =
                    h === "DATA" ? "entryDate" : h === "HORA" ? "entryTime" : h === "SAÍDA" ? "exitDate" : null;
                  return (
                    <th
                      key={h}
                      onClick={sortable ? () => toggleSort(sortable) : undefined}
                      className={`text-left text-[10px] font-bold uppercase tracking-wider text-white px-3 py-2.5 whitespace-nowrap ${
                        sortable ? "cursor-pointer select-none hover:bg-white/5" : ""
                      }`}
                      data-testid={sortable ? `sort-${sortable}` : undefined}
                    >
                      {h}
                      {sortable && <SortIcon active={sort.field === sortable} dir={sort.dir} />}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={HEADERS.length} className="text-center py-12 text-slate-400 text-sm">
                    Nenhuma OS encontrada.
                  </td>
                </tr>
              )}
              {filtered.map((o) => (
                <tr
                  key={o.id}
                  className="hover:bg-blue-50/40 border-b border-slate-100 last:border-0 transition-colors"
                >
                  <td className="px-3 py-2 text-xs whitespace-nowrap">
                    {editingEntryDateId === o.id ? (
                      <input
                        autoFocus
                        defaultValue={o.entryDate || ""}
                        placeholder="dd/mm/aaaa"
                        onBlur={(e) => {
                          const v = e.target.value.trim();
                          if (v && v !== o.entryDate) onUpdateOrder(o.id, { entryDate: v });
                          setEditingEntryDateId(null);
                        }}
                        onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                        className="w-24 text-xs border border-blue-300 rounded px-1.5 py-0.5 bg-blue-50 focus:outline-none"
                        data-testid={`edit-entrydate-${o.id}`}
                      />
                    ) : (
                      <button
                        onClick={() => setEditingEntryDateId(o.id)}
                        className="text-slate-700 hover:text-blue-700 hover:underline"
                        title="Clique para editar (admin)"
                      >
                        {o.entryDate}
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap tabular-nums">
                    {editingEntryTimeId === o.id ? (
                      <input
                        autoFocus
                        defaultValue={o.entryTime || ""}
                        placeholder="hh:mm"
                        onBlur={(e) => {
                          const v = e.target.value.trim();
                          if (v && v !== o.entryTime) onUpdateOrder(o.id, { entryTime: v });
                          setEditingEntryTimeId(null);
                        }}
                        onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                        className="w-16 text-xs border border-blue-300 rounded px-1.5 py-0.5 bg-blue-50 focus:outline-none"
                        data-testid={`edit-entrytime-${o.id}`}
                      />
                    ) : (
                      <button
                        onClick={() => setEditingEntryTimeId(o.id)}
                        className="text-slate-500 hover:text-blue-700 hover:underline"
                        title="Clique para editar (admin)"
                      >
                        {o.entryTime}
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {editingClientId === o.id ? (
                      <ClientInlineEditor
                        order={o}
                        onSave={(patch) => { onUpdateOrder(o.id, patch); setEditingClientId(null); }}
                        onCancel={() => setEditingClientId(null)}
                      />
                    ) : (
                      <button onClick={() => setEditingClientId(o.id)} className="text-left w-full" data-testid={`edit-client-${o.id}`} title="Clique para editar">
                        <div className="text-xs font-semibold text-slate-800 hover:text-blue-700">{o.clientName || "— editar —"}</div>
                        <div className="text-[10px] text-slate-400">{o.clientPhone || "—"}</div>
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {editingDeviceId === o.id ? (
                      <DeviceInlineEditor
                        order={o}
                        onSave={(patch) => { onUpdateOrder(o.id, patch); setEditingDeviceId(null); }}
                        onCancel={() => setEditingDeviceId(null)}
                      />
                    ) : (
                      <button onClick={() => setEditingDeviceId(o.id)} className="text-left w-full" data-testid={`edit-device-${o.id}`} title="Clique para editar">
                        <div className="text-xs text-slate-700 hover:text-blue-700">{o.model || "— editar —"}</div>
                        <div className="text-[10px] text-slate-400">{o.color || "—"}</div>
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {editingServicesId === o.id ? (
                      <ServicesInlineEditor
                        order={o}
                        onSave={(patch) => { onUpdateOrder(o.id, patch); setEditingServicesId(null); }}
                        onCancel={() => setEditingServicesId(null)}
                      />
                    ) : (
                      <button onClick={() => setEditingServicesId(o.id)} className="text-left w-full" data-testid={`edit-services-${o.id}`} title="Clique para editar (separe por vírgula)">
                        <div className="flex flex-wrap gap-1">
                          {(o.services || []).length === 0 ? (
                            <span className="text-[10px] italic text-slate-300 hover:text-blue-600">— editar —</span>
                          ) : (o.services || []).map((s) => (
                            <span key={s} className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 border border-blue-100 text-blue-700">
                              {s}
                            </span>
                          ))}
                        </div>
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap tabular-nums">
                    {editingValueId === o.id ? (
                      <input
                        autoFocus
                        type="text"
                        defaultValue={fmtBRL(o.total)}
                        onBlur={(e) => {
                          const v = parseFloat(e.target.value.replace(/\./g, "").replace(",", ".")) || 0;
                          onUpdateOrder(o.id, { total: v });
                          setEditingValueId(null);
                        }}
                        onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                        className="w-20 text-xs text-right border border-blue-300 rounded px-1.5 py-0.5 bg-blue-50 focus:outline-none"
                      />
                    ) : (
                      <button
                        onClick={() => setEditingValueId(o.id)}
                        className="text-xs font-semibold text-slate-800 hover:text-blue-700"
                      >
                        R$ {fmtBRL(o.total)}
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap tabular-nums">
                    {editingCostId === o.id ? (
                      <input
                        autoFocus
                        type="text"
                        defaultValue={o.costValue ? fmtBRL(o.costValue) : ""}
                        placeholder="0,00"
                        onBlur={(e) => {
                          const v = parseFloat(e.target.value.replace(/\./g, "").replace(",", ".")) || 0;
                          if (v !== (o.costValue || 0)) onUpdateOrder(o.id, { costValue: v });
                          setEditingCostId(null);
                        }}
                        onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                        className="w-20 text-xs text-right border border-rose-300 rounded px-1.5 py-0.5 bg-rose-50 focus:outline-none"
                        data-testid={`entry-cost-${o.id}`}
                      />
                    ) : (
                      <button
                        onClick={() => setEditingCostId(o.id)}
                        data-testid={`entry-cost-btn-${o.id}`}
                        className={o.costValue ? "text-xs font-semibold text-rose-700 hover:text-rose-900" : "text-xs text-slate-300 hover:text-rose-600"}
                      >
                        {o.costValue ? `R$ ${fmtBRL(o.costValue)}` : "— editar —"}
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {editingPayId === o.id ? (
                      <PayEditor
                        order={o}
                        onSave={(pay, desc) => {
                          onUpdateOrder(o.id, { payMethod: pay, cartaoDesc: desc });
                          setEditingPayId(null);
                        }}
                        onCancel={() => setEditingPayId(null)}
                      />
                    ) : (
                      <button onClick={() => setEditingPayId(o.id)}>
                        {o.payMethod ? (
                          <PayBadge payMethod={o.payMethod} cartaoDesc={o.cartaoDesc} />
                        ) : (
                          <span className="text-[10px] italic text-slate-400 hover:text-slate-600">— selecionar —</span>
                        )}
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <StatusBadge status={o.status} />
                  </td>
                  <td className="px-3 py-2 text-[11px] text-slate-500 whitespace-nowrap">
                    {o.exitDate ? (
                      <>
                        {o.exitDate}
                        <div className="text-[10px] text-slate-400">{o.exitTime}</div>
                      </>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-3 py-2 max-w-[180px]">
                    {editingObsId === o.id ? (
                      <input
                        autoFocus
                        defaultValue={o.observation || ""}
                        onBlur={(e) => {
                          onUpdateOrder(o.id, { observation: e.target.value });
                          setEditingObsId(null);
                        }}
                        onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                        className="w-full text-[11px] border border-blue-300 rounded px-1.5 py-0.5 bg-blue-50 focus:outline-none"
                      />
                    ) : (
                      <button
                        className="text-[11px] text-slate-600 hover:text-blue-700 truncate max-w-full text-left"
                        onClick={() => setEditingObsId(o.id)}
                        title={o.observation || "Adicionar obs."}
                      >
                        {o.observation || <span className="italic text-slate-300">— adicionar —</span>}
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-0.5">
                      <button
                        title="Ver detalhes"
                        onClick={() => onOpenDetail(o)}
                        data-testid={`view-detail-${o.id}`}
                        className="p-1.5 rounded hover:bg-blue-50 text-slate-400 hover:text-blue-600 transition-colors"
                      >
                        <Eye className="w-3.5 h-3.5" />
                      </button>
                      <button
                        title="Imprimir etiqueta"
                        onClick={() => printOrderLabel(o)}
                        className="p-1.5 rounded hover:bg-purple-50 text-slate-400 hover:text-purple-700 transition-colors"
                        data-testid={`label-${o.id}`}
                      >
                        <Tag className="w-3.5 h-3.5" />
                      </button>
                      {o.status === "ready" && (
                        <a
                          href={waLink(
                            o.clientPhone,
                            `Olá ${o.clientName}, seu ${o.model} está pronto para retirada! 🎉`
                          )}
                          target="_blank"
                          rel="noreferrer"
                          title="Avisar cliente (WhatsApp)"
                          className="p-1.5 rounded hover:bg-emerald-50 text-slate-400 hover:text-emerald-600 transition-colors"
                          data-testid={`whatsapp-${o.id}`}
                        >
                          <MessageCircle className="w-3.5 h-3.5" />
                        </a>
                      )}
                      {o.status !== "delivered" && (
                        <button
                          title={o.status === "received" ? "Marcar como pronto" : "Registrar saída"}
                          onClick={() => advance(o)}
                          data-testid={`advance-${o.id}`}
                          className="p-1.5 rounded hover:bg-emerald-50 text-slate-400 hover:text-emerald-600 transition-colors"
                        >
                          {o.status === "received" ? (
                            <CheckCircle2 className="w-3.5 h-3.5" />
                          ) : (
                            <TruckIcon className="w-3.5 h-3.5" />
                          )}
                        </button>
                      )}
                      {o.status !== "received" && (
                        <button
                          title="Reverter status"
                          onClick={() => onRevertStatus(o.id)}
                          className="p-1.5 rounded hover:bg-amber-50 text-slate-400 hover:text-amber-600 transition-colors"
                        >
                          <ArrowLeft className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button
                        title="Excluir"
                        onClick={() => setConfirmDelete(o)}
                        className="p-1.5 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-600 transition-colors"
                        data-testid={`delete-${o.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmDialog
        open={!!confirmDelete}
        title="Excluir ordem de serviço"
        message={
          confirmDelete
            ? `Tem certeza que deseja excluir a OS ${confirmDelete.orderNumber || ""} de ${confirmDelete.clientName}? Essa ação não pode ser desfeita.`
            : ""
        }
        confirmLabel="Excluir"
        onCancel={() => setConfirmDelete(null)}
        onConfirm={async () => {
          const id = confirmDelete.id;
          setConfirmDelete(null);
          await onDeleteOrder(id);
        }}
      />
    </div>
  );
}

function ClientInlineEditor({ order, onSave, onCancel }) {
  const [name, setName] = useState(order.clientName || "");
  const [phone, setPhone] = useState(order.clientPhone || "");
  const commit = () => {
    const patch = {};
    if (name !== (order.clientName || "")) patch.clientName = name;
    if (phone !== (order.clientPhone || "")) patch.clientPhone = phone;
    if (Object.keys(patch).length === 0) onCancel(); else onSave(patch);
  };
  return (
    <div className="flex flex-col gap-1"
      onBlur={(e) => { const n = e.relatedTarget; if (n && e.currentTarget.contains(n)) return; commit(); }}
      onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") onCancel(); }}>
      <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome" className="w-full text-xs border border-blue-300 rounded px-1.5 py-0.5 bg-blue-50 focus:outline-none" data-testid={`edit-client-name-${order.id}`} />
      <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Telefone" className="w-full text-[10px] border border-blue-200 rounded px-1.5 py-0.5 bg-blue-50/50 focus:outline-none" data-testid={`edit-client-phone-${order.id}`} />
    </div>
  );
}

function DeviceInlineEditor({ order, onSave, onCancel }) {
  const [model, setModel] = useState(order.model || "");
  const [color, setColor] = useState(order.color || "");
  const commit = () => {
    const patch = {};
    if (model !== (order.model || "")) patch.model = model;
    if (color !== (order.color || "")) patch.color = color;
    if (Object.keys(patch).length === 0) onCancel(); else onSave(patch);
  };
  return (
    <div className="flex flex-col gap-1"
      onBlur={(e) => { const n = e.relatedTarget; if (n && e.currentTarget.contains(n)) return; commit(); }}
      onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") onCancel(); }}>
      <input autoFocus value={model} onChange={(e) => setModel(e.target.value)} placeholder="Modelo" className="w-full text-xs border border-blue-300 rounded px-1.5 py-0.5 bg-blue-50 focus:outline-none" data-testid={`edit-device-model-${order.id}`} />
      <input value={color} onChange={(e) => setColor(e.target.value)} placeholder="Cor" className="w-full text-[10px] border border-blue-200 rounded px-1.5 py-0.5 bg-blue-50/50 focus:outline-none" data-testid={`edit-device-color-${order.id}`} />
    </div>
  );
}

function ServicesInlineEditor({ order, onSave, onCancel }) {
  const [text, setText] = useState((order.services || []).join(", "));
  const commit = () => {
    const list = text.split(",").map((s) => s.trim()).filter(Boolean);
    const prev = order.services || [];
    if (list.length === prev.length && list.every((s, i) => s === prev[i])) onCancel();
    else onSave({ services: list });
  };
  return (
    <textarea
      autoFocus
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); commit(); } if (e.key === "Escape") onCancel(); }}
      placeholder="Ex.: Troca de tela, Troca de bateria"
      rows={2}
      className="w-full text-[11px] border border-blue-300 rounded px-1.5 py-0.5 bg-blue-50 focus:outline-none resize-none"
      data-testid={`edit-services-input-${order.id}`}
    />
  );
}

